import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
} from 'react'
import * as localStorageUtils from '@/common/local-storage/local-storage-utils'
import { localStorageKeys } from '@/core/configs/local-storage-keys'

const RIGHT_PANEL_WIDTH_VAR = '--right-panel-width'

interface RightPanelContextValue {
  content: ReactNode | null
  openPanel: (content: ReactNode) => void
  closePanel: () => void
  panelWidth: number
  setPanelWidth: (width: number) => void
}

export const RightPanelContext = createContext<RightPanelContextValue | null>(null)

export function RightPanelProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<ReactNode | null>(null)
  const [panelWidth, setPanelWidthState] = useState(
    () => localStorageUtils.readNumber(localStorageKeys.RightPanelWidth) ?? 660,
  )

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(RIGHT_PANEL_WIDTH_VAR, `${panelWidth}px`)
  }, [panelWidth])

  const setPanelWidth = useCallback((width: number) => {
    setPanelWidthState(width)
    localStorageUtils.writeNumber(localStorageKeys.RightPanelWidth, width)
  }, [])

  return (
    <RightPanelContext.Provider
      value={{
        content,
        openPanel: setContent,
        closePanel: () => setContent(null),
        panelWidth,
        setPanelWidth,
      }}
    >
      {children}
    </RightPanelContext.Provider>
  )
}

export function useRightPanel() {
  return useContext(RightPanelContext)!
}
